import React, { useRef, useState } from 'react'
import TrayArrowDown from 'mdi-material-ui/TrayArrowDown'
import { ArrowExpandDown, Eraser, Stamper } from 'mdi-material-ui'
import { Box, Button, Stack, Typography } from '@mui/material'
import { useMutation } from 'react-query'
import { updateUser } from '@/@core/services/app.service'
import { useTranslation } from 'react-i18next'
import { message } from 'antd'
import { PanoramaSharp } from '@mui/icons-material'
import { useDropzone } from 'react-dropzone'

interface Props {
  getImgUrl: (img: string) => void
}

const ElectronicSignature = (props: Props) => {
  const { t } = useTranslation()
  const [messageApi, contextHolder] = message.useMessage()
  const canvasRef = useRef<HTMLCanvasElement>(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [signatureImage, setSignatureImage] = useState<string | null>(null)
  const [showStamp, setShowStamp] = useState(false)
  const [stampPosition, setStampPosition] = useState({ x: 0, y: 0 })
  const user = JSON.parse(localStorage.getItem('user') || '{}')
  const [imgSrc, setImgSrc] = React.useState('')

  // ** Hooks
  const { getRootProps, getInputProps } = useDropzone({
    onDrop: (acceptedFiles: File[]) => {
      if (acceptedFiles.length) {
        const file = acceptedFiles[0]

        if (file) {
          const reader = new FileReader()

          reader.onload = (e: any) => {
            setImgSrc(e.target.result)
          }

          reader.onerror = (error) => {
            console.error('Error reading file:', error)
          }

          reader.readAsDataURL(file) // 读取文件为 Data URL (Base64)
        }
      }
    }
  })
  const updateUserMutation = useMutation({
    mutationFn: (params: any) => updateUser(params),
    onSuccess: (data, params) => {
      messageApi.success(t('Edit success'))
      const user = JSON.parse(localStorage.getItem('user') || '{}')
      localStorage.setItem(
        'user',
        JSON.stringify({
          ...user,
          signatureImgUrl: params.signatureImgUrl
        })
      )
    },
    onError: (error: any) => {
      messageApi.error(error.response.data.message)
    }
  })

  // 初始化画布
  const initCanvas = () => {
    const canvas = canvasRef.current
    if (!canvas) return

    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.lineWidth = 8
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#000000'
    ctx.fillStyle = 'transparent'
    ctx.clearRect(0, 0, canvas.width, canvas.height)
  }

  // 开始绘制
  const startDrawing = (e: any) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const ctx = canvas.getContext('2d')
    if (!ctx) return
    ctx.lineWidth = 8
    ctx.lineCap = 'round'
    ctx.strokeStyle = '#000000'
    ctx.fillStyle = 'transparent'
    if (!ctx) return

    setIsDrawing(true)
    ctx.beginPath()
    ctx.moveTo(e.clientX - rect.left, e.clientY - rect.top)
  }

  // 绘制中
  const draw = (e: any) => {
    if (!isDrawing) return
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    ctx.lineTo(e.clientX - rect.left, e.clientY - rect.top)
    ctx.stroke()
  }

  // 结束绘制
  const stopDrawing = () => {
    const canvas = canvasRef.current
    if (!canvas) return
    const ctx = canvas.getContext('2d')
    if (!ctx) return

    setIsDrawing(false)
    ctx.closePath()
  }

  // 清除画布
  const clearCanvas = () => {
    initCanvas()
    setSignatureImage(null)
  }

  // 保存签名
  const saveSignature = () => {
    if (imgSrc) {
      console.log(imgSrc)

      setSignatureImage(imgSrc)
      props.getImgUrl(imgSrc)

      const user = JSON.parse(localStorage.getItem('user') || '{}')

      const params = {
        ...user,
        signatureImgUrl: imgSrc
      }

      updateUserMutation.mutateAsync(params)
      return
    }
    const canvas = canvasRef.current

    if (!canvas) return
    const dataURL = canvas.toDataURL('image/png')
    setSignatureImage(dataURL)
    props.getImgUrl(dataURL)

    const user = JSON.parse(localStorage.getItem('user') || '{}')

    const params = {
      ...user,
      signatureImgUrl: dataURL
    }

    updateUserMutation.mutateAsync(params)
  }

  // 沿用以前签名
  const useBefforeSignature = () => {
    setSignatureImage(user.signatureImgUrl)
    props.getImgUrl(user.signatureImgUrl)
  }

  // 添加印章
  const addStamp = (e: any) => {
    const canvas = canvasRef.current
    if (!canvas) return
    const rect = canvas.getBoundingClientRect()
    if (!rect) return
    setStampPosition({
      x: e.clientX - rect.left - 50,
      y: e.clientY - rect.top - 50
    })
    setShowStamp(true)
  }

  // 生成印章SVG
  const StampSVG = () => (
    <svg
      width="100"
      height="100"
      viewBox="0 0 100 100"
      style={{
        position: 'absolute',
        left: stampPosition.x,
        top: stampPosition.y,
        pointerEvents: 'none'
      }}
    >
      <defs>
        <radialGradient
          id="stampGradient"
          cx="50%"
          cy="50%"
          r="50%"
          fx="50%"
          fy="50%"
        >
          <stop offset="0%" stopColor="#d32f2f" stopOpacity="0.8" />

          <stop offset="100%" stopColor="#b71c1c" stopOpacity="0.5" />
        </radialGradient>
      </defs>

      {/* 印章外圆 */}
      <circle
        cx="50"
        cy="50"
        r="45"
        fill="none"
        stroke="#b71c1c"
        strokeWidth="3"
      />

      {/* 印章内圆 */}
      <circle cx="50" cy="50" r="35" fill="url(#stampGradient)" />

      {/* 五角星 */}
      <polygon
        points="50,15 54,35 74,35 57,46 62,66 50,55 38,66 43,46 26,35 46,35"
        fill="#ffd600"
        stroke="#ffab00"
        strokeWidth="1"
      />

      {/* 印章文字 */}
      <text
        x="50"
        y="85"
        textAnchor="middle"
        fill="#ffffff"
        fontSize="12"
        fontWeight="bold"
      >
        公司公章
      </text>
    </svg>
  )

  return (
    <div>
      {contextHolder}

      <Typography component={'h2'} fontSize={24} fontWeight={'bold'}>
        <Stamper color="error" /> {t('Electronic signature')}
      </Typography>

      <Box
        sx={{
          border: '1px solid #000000',
          borderRadius: 4,
          padding: 2,
          marginBottom: 4
        }}
      >
        {imgSrc ? (
          <img
            src={imgSrc}
            alt="签名预览"
            style={{
              width: 720,
              height: 300
            }}
          />
        ) : (
          <canvas
            ref={canvasRef}
            width={720}
            height={350}
            onMouseDown={startDrawing}
            onMouseMove={draw}
            onMouseUp={stopDrawing}
            onMouseLeave={stopDrawing}
            className="cursor-crosshair bg-gray-50"
          />
        )}

        {showStamp && <StampSVG />}

        {/* {signatureImage && (
          <div>
            <img src={signatureImage} alt="签名预览" />
          </div>
        )} */}
      </Box>

      <Stack spacing={2} direction="row">
        <Button variant="contained" color="error" onClick={clearCanvas}>
          <Eraser /> {t('Clear the canvas')}
        </Button>

        <Button variant="contained" color="primary" onClick={saveSignature}>
          <TrayArrowDown /> {t('Save signature')}
        </Button>

        {user && user.signatureImgUrl && (
          <Button
            variant="contained"
            color="info"
            onClick={useBefforeSignature}
          >
            <ArrowExpandDown /> {t('Use before signature')}
          </Button>
        )}

        <div
          {...getRootProps({
            className: 'dropzone'
          })}
        >
          <input {...getInputProps()} accept={'.png,.img'} />

          <Button variant="contained" color="primary">
            <TrayArrowDown /> {t('上传签名图片')}
          </Button>
        </div>

        {/* <Button
          variant="contained"
          color="success"
          onClick={(e) => {
            const canvas = canvasRef.current
            if (!canvas) return
            const rect = canvas.getBoundingClientRect()
            alert(
              `请在画布上点击放置印章\n当前画布尺寸: ${rect.width}x${rect.height}`
            )
            addStamp(e)
          }}
        >
          <Stamper /> 添加印章
        </Button> */}
      </Stack>

      <Box
        sx={{
          padding: 2,
          bgcolor: 'rgb(243 244 246 / var(--tw-bg-opacity, 1))',
          borderRadius: 'var(--radius)',
          mt: 6
        }}
      >
        <h2
          style={{
            fontSize: 20,
            fontWeight: 'bold'
          }}
        >
          {t('Use instruction')}
        </h2>

        <ul
          style={{
            listStyleType: 'disc',
            fontSize: 16,
            paddingLeft: 20
          }}
        >
          <li
            style={{
              marginBottom: 8
            }}
          >
            {t('Drag the mouse on the canvas to sign.')}
          </li>

          <li
            style={{
              marginBottom: 8
            }}
          >
            {t("Use 'Clear Canvas' to start over")}
          </li>
        </ul>
      </Box>
    </div>
  )
}

export default ElectronicSignature
